<template>
    <v-dialog
        v-model="dialog"
        max-width="500px"
        min-height="200px"
        :opacity="0.8"
        class="workspace-dialog"
        @after-leave="$emit('update:modelValue', false)"
    >
        <v-card class="workspace-dialog-card pa-4">
            <v-btn icon size="small" variant="text" class="close-button" @click="dialog = false">
                <v-icon>mdi-close</v-icon>
            </v-btn>

            <v-card-title class="px-1 py-0">
                <h4>
                    {{ t('workspace.selection.create_workspace_dialog.title') }}
                </h4>
            </v-card-title>

            <v-card-subtitle class="px-1">
                {{ t('workspace.selection.create_workspace_dialog.subtitle') }}
            </v-card-subtitle>

            <div class="workspace-content px-1">
                <v-spacer />
                <v-text-field
                    id="workspace-name-input"
                    v-model="workspaceName"
                    class="flex-grow-0"
                    :placeholder="
                        t(
                            'workspace.selection.create_workspace_dialog.name_of_workspace_field_title',
                        )
                    "
                    counter
                    variant="outlined"
                    density="compact"
                    maxlength="60"
                    :rules="[
                        (v) =>
                            v.length >= 3 ||
                            t('workspace.selection.create_workspace_dialog.name_min_length'),
                    ]"
                    hide-details="auto"
                    @keydown.enter="createNewWorkspace"
                ></v-text-field>

                <v-spacer />

                <div class="d-flex justify-end">
                    <ButtonComponent
                        :disabled="workspaceName.length < 3"
                        :loading="isCreatingNewWorkspace"
                        name="button-create-workspace"
                        @click="createNewWorkspace"
                    >
                        {{ t('common.create') }}
                    </ButtonComponent>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ButtonComponent from '@/components/utils/buttons/ButtonComponent.vue';
import { useUserStore } from '@/stores/user';
import { useWorkspaceStore } from '@/stores/workspace';
import { mainAxios as axios } from '@/assets/js/axios_config';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true,
    },
});

const emit = defineEmits(['update:modelValue', 'workspace-created']);

const userStore = useUserStore();
const workspaceStore = useWorkspaceStore();
const workspaceName = ref('');
const isCreatingNewWorkspace = ref(false);

const dialog = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

watch(dialog, (newVal) => {
    if (!newVal) {
        workspaceName.value = '';
    }
});

async function createNewWorkspace() {
    isCreatingNewWorkspace.value = true;
    try {
        const response = await axios.post('/workspace/update/', { name: workspaceName.value });
        await axios.post(`/workspace/user/select/${response.data.id}`);
        await userStore.loadData();
        await workspaceStore.handleWorkspaceChange();

        emit('workspace-created');
        dialog.value = false;
    } catch (error) {
        console.error(error);
    }
    isCreatingNewWorkspace.value = false;
}
</script>

<style lang="scss">
.workspace-dialog {
    height: 100%;
    min-height: 200px;

    &-card {
        height: 100%;
        display: flex;
        flex-direction: column;
        border-radius: 8px !important;
        position: relative;

        .close-button {
            position: absolute;
            top: 8px;
            right: 8px;
        }
    }
}

.workspace-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
}

#workspace-name-input {
    :deep(.v-input__control) {
        display: inherit;
    }
}
</style>
